import type { Category } from '@/types'

/** Resolve a category accent (configured color or stable hash fallback). */
export function categoryColor(cat: Category | undefined, fallback: string): string {
  const raw = cat?.color?.trim()
  if (raw) {
    if (/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(raw)) return raw
    if (/^[0-9A-Fa-f]{6}$/.test(raw)) return `#${raw}`
    if (/^[0-9A-Fa-f]{3}$/.test(raw)) {
      const [a, b, c] = raw.split('')
      return `#${a}${a}${b}${b}${c}${c}`
    }
  }
  let h = 0
  const s = (cat?.name ?? fallback) || 'x'
  for (let i = 0; i < s.length; i++) h = (h + s.charCodeAt(i) * 17) % 360
  return `hsl(${h} 45% 42%)`
}

/**
 * The card background as a real CSS color.
 *
 * `--card` is a bare HSL triplet (`0 0% 100%`), not a color — Tailwind wraps it
 * in `hsl()` at use site. Five call sites dropped that wrapper and passed
 * `var(--card)` straight into `color-mix()`, which makes the whole function
 * invalid on EVERY browser, so their tint was silently discarded everywhere.
 * Use this instead of writing the token by hand.
 */
export const CARD_SURFACE = 'hsl(var(--card))'

/**
 * Darken an accent, in JS rather than with `color-mix()` (F-FE-N152).
 *
 * The selected category chip painted itself with
 * `color-mix(in srgb, accent 85%, black)` as an INLINE style, over `text-white`
 * and a white stripe. `color-mix()` landed in Chrome 111; the tablets run inside
 * an Android System WebView and `minSdk` is 24, so an older engine is entirely
 * possible — and where it is unsupported the declaration is dropped, leaving
 * white text and a white stripe on a white pane in a chip that still occupies
 * its full height. The tenant reported it as "the category disappears, leaving
 * a weird gap", which is exactly what that looks like.
 *
 * A tint that fails to apply is cosmetic. A chip that becomes invisible is not,
 * so the selected state must not depend on a CSS feature at all. Unselected
 * chips keep `color-mix` for their subtle wash: they carry a border and
 * `text-foreground`, so losing it costs nothing legible.
 *
 * Handles the two shapes `categoryColor` can return, and returns anything else
 * untouched — an un-darkened chip is still perfectly readable, which is the
 * right way to be wrong here.
 */
export function darkenAccent(accent: string, factor = 0.85): string {
  const raw = (accent ?? '').trim()

  const hex = /^#([0-9A-Fa-f]{6})$/.exec(raw)
  if (hex) {
    const n = parseInt(hex[1], 16)
    const ch = [(n >> 16) & 255, (n >> 8) & 255, n & 255]
      .map((c) => Math.max(0, Math.min(255, Math.round(c * factor))))
      .map((c) => c.toString(16).padStart(2, '0'))
      .join('')
    return `#${ch}`
  }

  // `hsl(210 45% 42%)` — the space-separated form categoryColor emits.
  const hsl = /^hsl\(\s*([\d.]+)\s+([\d.]+)%\s+([\d.]+)%\s*\)$/.exec(raw)
  if (hsl) {
    const l = Math.max(0, Math.min(100, Number(hsl[3]) * factor))
    return `hsl(${hsl[1]} ${hsl[2]}% ${Math.round(l * 10) / 10}%)`
  }

  return raw
}

/** Soft card tint from a category accent (hex or hsl). */
export function categorySurfaceStyle(accent: string): { background: string } {
  return {
    background: `linear-gradient(135deg, color-mix(in srgb, ${accent} 30%, ${CARD_SURFACE}) 0%, ${CARD_SURFACE} 58%)`,
  }
}
